import * as ReactRedux from 'react-redux'
import * as ReactRouter from 'react-router-dom'
import LazyLoad from 'react-lazyload'
import React from 'react'

import * as Common from '@rushplay/common'
import * as Herz from '@rushplay/herz'
import styled from '@emotion/styled'
import { css } from '@styled-system/css'

import * as Configuration from './configuration'
import { AppVersion } from './app-version'
import { ClientType } from './constants'
import { HtmlContent } from './html-content'
import { XcmSeal } from './xcm-seal'
import { useGameInfoUrlQuery } from './use-game-info-url-query'

const GCB_ID =
  'ZXlKcGRpSTZJbTV1ZERadVZsaDFkVFZSTjNGMlJXSjRka0pNU0hjOVBTSXNJblpoYkhWbElqb2lRVTkxUTBSMlF6WTBXRTlUT0ZWVWVTdFlXVnBWZHowOUlpd2liV0ZqSWpvaU5qSTNPREl5WWprek1EYzVZbU13WlRNM01tSmpNRFZsWWpBNFltTXhaVFF6TURnNU1qYzNOR1JtWlRFNU5HRXhaRFEzTURsaVpqUmlOalpqTUdabU9TSXNJblJoWnlJNklpSjk='

const LinkWrapper = styled.div`
  ${css({
    p: 1,
  })}
  opacity: 0.5;
  transition: opacity 75ms ease-in;

  &:hover,
  &:active {
    opacity: 0.9;
  }
`

const translationKeys = [
  'footer.terms-and-conditions',
  'footer.about-us',
  'footer.cookie-policy',
  'footer.faq',
  'footer.privacy-policy',
  'footer.responsible-gaming',
  'footer.security',
  'footer.affiliate',
  'footer.legal-text.curacao',
  '18-plus',
  'footer.self-exclusion',
  'footer.accounts-payouts-bonuses',
]

export function Footer() {
  const translate = Herz.I18n.useTranslate()
  const gameInfoQuery = useGameInfoUrlQuery()
  const clientType = ReactRedux.useSelector(state =>
    Configuration.getClientType(state.configuration)
  )
  const isMobile = ClientType.MOBILE_BROWSER === clientType
  const { authenticated } = Herz.Auth.useSession()
  const location = ReactRouter.useLocation()

  /**
   * Hides the footer:
   * - When playing any game on mobile
   */

  if (
    location.pathname.match('starsplaypromo') ||
    (isMobile &&
      ((gameInfoQuery.referrer === 'live-casino' && authenticated) ||
        gameInfoQuery.referrer === 'casino'))
  ) {
    return null
  }

  return (
    <Common.Box
      display="flex"
      backgroundColor="footer-background"
      padding={[1, 2]}
      flexDirection="column"
      alignItems="center"
      as="footer"
    >
      <Common.Box
        as="nav"
        color="#666975"
        display="flex"
        flexWrap="wrap"
        fontSize={1}
        justifyContent="center"
        pt={0}
        opacity="0.8"
      >
        <ReactRouter.Link
          to="/terms-and-conditions"
          data-testid="footer.link.terms"
        >
          <LinkWrapper>{translate('footer.terms-and-conditions')}</LinkWrapper>
        </ReactRouter.Link>
        <ReactRouter.Link to="/about" data-testid="footer.link.about">
          <LinkWrapper>{translate('footer.about-us')}</LinkWrapper>
        </ReactRouter.Link>
        <ReactRouter.Link
          to="/cookie-policy"
          data-testid="footer.link.cookie-policy"
        >
          <LinkWrapper>{translate('footer.cookie-policy')}</LinkWrapper>
        </ReactRouter.Link>
        <ReactRouter.Link to="/faq" data-testid="footer.link.faq">
          <LinkWrapper>{translate('footer.faq')}</LinkWrapper>
        </ReactRouter.Link>
        <ReactRouter.Link
          to="/privacy-policy"
          data-testid="footer.link.privacy-policy"
        >
          <LinkWrapper>{translate('footer.privacy-policy')}</LinkWrapper>
        </ReactRouter.Link>
        <ReactRouter.Link
          to="/responsible-gaming"
          data-testid="footer.link.responsible-gaming"
        >
          <LinkWrapper>{translate('footer.responsible-gaming')}</LinkWrapper>
        </ReactRouter.Link>
        <ReactRouter.Link
          to="/self-exclusion"
          data-testid="footer.link.self-exclusion"
        >
          <LinkWrapper>{translate('footer.self-exclusion')}</LinkWrapper>
        </ReactRouter.Link>
        <ReactRouter.Link
          to="/accounts-payouts-bonuses"
          data-testid="footer.link.accounts-payouts-bonuses"
        >
          <LinkWrapper>
            {translate('footer.accounts-payouts-bonuses')}
          </LinkWrapper>
        </ReactRouter.Link>
        <a
          href="https://heropartners.io/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer.link.affiliate"
        >
          <LinkWrapper>{translate('footer.affiliate')}</LinkWrapper>
        </a>
      </Common.Box>

      <Common.Box
        fontSize={1}
        color="#666975"
        opacity="0.5"
        pt={2}
        textAlign="center"
        mx="auto"
        maxWidth="700px"
      >
        <HtmlContent
          as="small"
          html={{
            __html: translate('footer.legal-text.curacao'),
          }}
        />
      </Common.Box>
      <LazyLoad once offset={100}>
        <Common.Box
          display="flex"
          pt={2}
          justifyContent="center"
          alignItems="center"
        >
          <Common.Box
            as="a"
            href={`https://cert.gcb.cw/certificate?id=${GCB_ID}`}
            target="_blank"
            rel="noopener noreferrer"
            display="flex"
          >
            <Common.Box
              as="img"
              alt="GCB license"
              src="/images/gcb.avif"
              height="34px"
              width="auto"
            />
          </Common.Box>
          <Common.Box px={0}>
            <XcmSeal />
          </Common.Box>
          <ReactRouter.Link to="/responsible-gaming">
            <Common.Box
              as="img"
              alt="18+"
              src={translate('18-plus')}
              height="34px"
              mx={1}
            />
          </ReactRouter.Link>
        </Common.Box>
      </LazyLoad>
      <Common.Box textAlign="center">
        <AppVersion />
      </Common.Box>
    </Common.Box>
  )
}

Herz.I18n.Loader.preload(translationKeys, Footer)

// for @loadable/components
export default Footer
